// ─────────────────────────────────────────────────────────────────────────────
// SAP Insights POC — Service Layer
// Calls the same backend service as automationInsightsService.ts
// (topsreliabilityframework-automationapi), different router paths
// (/automation/api/panels/sap-platform-table(/groups)). No automation-only
// fields on any type here (no auto_fulfilled/sap_resolved/overall_resolved/
// resolution_pct) — there is no automation on this pipeline.
// ─────────────────────────────────────────────────────────────────────────────

import { SAP_INSIGHTS_ENDPOINTS } from '../config/api';
import { authFetch } from './authFetch';

export type SapTicketType = 'ALL' | 'INC' | 'RITM';
export type SapSourceFilterType = 'all' | 'tori' | 'human';
export type SapBasis = 'created' | 'resolved';

function camelToSnakeCase(key: string): string {
  return key.replace(/([A-Z])/g, '_$1').toLowerCase();
}

function buildQueryString<T extends object>(params: T): string {
  const search = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.append(camelToSnakeCase(key), String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export interface SapPlatformMetrics {
  vol_created: number;
  vol_resolved: number;
  tori_vol: number;
  cne_count: number;
  cne_pct: number;
  reopen_count: number;
  reopen_pct: number;
  /** Created view only (null on the Resolved view): tickets created in the
   * selected range, sitting in the platform's own assignment groups, still
   * open by real SNOW state. Never counts SLA-only / other teams' groups. */
  open_tickets: number | null;
  /** Resolved View only (basis='resolved') — undefined/null under
   * basis='created'. Same shape as the automation page's sap_resolved/
   * overall_resolved/resolution_pct, renamed since there's no automation
   * tiering concept here: sap_resolved (labeled "SAP Resolution" in the UI)
   * = resolved while the ticket's current/final assignment_group was one of
   * this platform's own tracked groups. overall_resolved = resolved after
   * the ticket's SLA history touched one of those groups at any point,
   * regardless of who/where it was finally closed (always >= sap_resolved).
   * resolution_pct = sap_resolved / overall_resolved. Added 2026-10. */
  sap_resolved?: number | null;
  overall_resolved?: number | null;
  resolution_pct?: number | null;
  avg_mttr: number | null;
  avg_mttr_p50: number | null;
  avg_mttr_p80: number | null;
  avg_mttr_p85: number | null;
  avg_mttr_p90: number | null;
}

export interface SapPlatformTableRow extends SapPlatformMetrics {
  platform_key: string;
  platform_name: string;
  ticket_type: SapTicketType;
}

export interface SapPlatformTableData {
  ticket_type: SapTicketType;
  source_filter: SapSourceFilterType;
  date_from: string;
  date_to: string;
  basis: SapBasis;
  rows: SapPlatformTableRow[];
  grand_total?: SapPlatformMetrics;
}

export interface SapPlatformTableParams {
  ticketType: SapTicketType;
  sourceFilter?: SapSourceFilterType;
  timeFilterType?: 'day' | 'week' | 'month';
  selectedPeriods?: string[];
  dateFrom?: string;
  dateTo?: string;
  basis?: SapBasis;
}

/** Fetch the SAP Insights Platform Table — one row per SAP platform + grand total. */
export async function fetchSapPlatformTable(params: SapPlatformTableParams): Promise<SapPlatformTableData> {
  const url = `${SAP_INSIGHTS_ENDPOINTS.PANELS.PLATFORM_TABLE}${buildQueryString(params)}`;
  const response = await authFetch(url);

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`SAP Insights API error (${response.status}): ${body || response.statusText}`);
  }

  return response.json();
}

export interface SapPlatformTableGroupRow extends SapPlatformMetrics {
  assignment_group: string;
}

export interface SapPlatformTableGroupsData {
  platform_key: string;
  platform_name: string;
  ticket_type: SapTicketType;
  source_filter: SapSourceFilterType;
  date_from: string;
  date_to: string;
  basis: SapBasis;
  rows: SapPlatformTableGroupRow[];
}

export interface SapPlatformTableGroupsParams {
  platform: string;
  ticketType: SapTicketType;
  sourceFilter?: SapSourceFilterType;
  timeFilterType?: 'day' | 'week' | 'month';
  selectedPeriods?: string[];
  dateFrom?: string;
  dateTo?: string;
  basis?: SapBasis;
}

/** Assignment-group breakdown for one SAP platform — the data behind an
 * expanded row in the SAP Platform Table. */
export async function fetchSapPlatformTableGroups(
  params: SapPlatformTableGroupsParams
): Promise<SapPlatformTableGroupsData> {
  const url = `${SAP_INSIGHTS_ENDPOINTS.PANELS.PLATFORM_TABLE_GROUPS}${buildQueryString(params)}`;
  const response = await authFetch(url);

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`SAP Insights API error (${response.status}): ${body || response.statusText}`);
  }

  return response.json();
}

// ── Ticket drill-down ─────────────────────────────────────────────────────────

export interface DrilldownTicket {
  ticket_number: string;
  ticket_type: string;
  snow_url: string;
  assignment_group: string;
  state: string;
  assigned_to: string;
  cmdb_ci: string;
  short_description: string;
  mttr_hours: number | null;
  reopen_count: number;
  cne_raised: boolean;
  created_date: string | null;
  resolved_date: string | null;
  source: string;
  // Automation Insights only:
  outcome?: string;
  automation_name?: string | null;
}

export interface DrilldownResponse {
  total: number;
  page: number;
  page_size: number;
  tickets: DrilldownTicket[];
}

export interface SapDrilldownParams {
  platform: string;
  column: string;
  basis: string;
  ticketType: string;
  sourceFilter: string;
  timeFilterType?: string;
  selectedPeriods?: string[];
  dateFrom?: string;
  dateTo?: string;
  assignmentGroup?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchSapTicketDrilldown(params: SapDrilldownParams): Promise<DrilldownResponse> {
  const url = `${SAP_INSIGHTS_ENDPOINTS.PANELS.PLATFORM_TABLE_TICKETS}${buildQueryString(params)}`;
  const response = await authFetch(url);
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`SAP drilldown error (${response.status}): ${body || response.statusText}`);
  }
  return response.json();
}
